import { AzureGateway } from '@/components/file-manager/azure-blob-storage/AzureGateway'
import { AccessUrls, AzureResponse, FileEntry } from '@/components/file-manager/azure-blob-storage/types'
import FileSystemItem from 'devextreme/file_management/file_system_item'

export class AzureFileSystem {
  gateway: AzureGateway

  EMPTY_DIR_DUMMY_BLOB_NAME: string

  constructor(azureGateway: AzureGateway) {
    this.gateway = azureGateway
    this.EMPTY_DIR_DUMMY_BLOB_NAME = 'aspxAzureEmptyFolderBlob'
  }

  getItems(path: string): Promise<FileSystemItem[]> {
    const prefix = this.getDirectoryBlobName(path)
    return this.gateway.getBlobList(prefix).then((entries) => this.getDataObjectsFromEntries(entries, prefix))
  }

  createDirectory(path: string, name: string): Promise<AzureResponse> {
    const blobName = path ? `${path}/${name}` : name
    return this.gateway.createDirectoryBlob(blobName)
  }

  renameFile(path: string, name: string): Promise<AzureResponse> {
    const newPath = this.getPathWithNewName(path, name)
    return this.moveFile(path, newPath)
  }

  renameDirectory(path: string, name: string): Promise<AzureResponse[]> {
    const newPath = this.getPathWithNewName(path, name)
    return this.moveDirectory(path, newPath)
  }

  getPathWithNewName(path: string, name: string): string {
    const parts = path.split('/')
    parts[parts.length - 1] = name
    return parts.join('/')
  }

  deleteFile(path: string): Promise<AzureResponse> {
    return this.gateway.deleteBlob(path)
  }

  deleteDirectory(path: string): Promise<AzureResponse[]> {
    const prefix = this.getDirectoryBlobName(path)
    return this.executeActionForEachEntry(prefix, (entry: any) => this.gateway.deleteBlob(entry.name))
  }

  copyFile(sourcePath: string, destinationPath: string): Promise<AzureResponse> {
    return this.gateway.copyBlob(sourcePath, destinationPath)
  }

  copyDirectory(sourcePath: string, destinationPath: string): Promise<AzureResponse[]> {
    const prefix = this.getDirectoryBlobName(sourcePath)
    const destinationKey = this.getDirectoryBlobName(destinationPath)
    return this.executeActionForEachEntry(prefix, (entry: any) => this.copyEntry(entry, prefix, destinationKey))
  }

  copyEntry(entry: FileSystemItem, sourceKey: string, destinationKey: string): Promise<AzureResponse> {
    const restName = entry.name.substring(sourceKey.length)
    const newDestinationKey = destinationKey + restName
    return this.gateway.copyBlob(entry.name, newDestinationKey)
  }

  moveFile(sourcePath: string, destinationPath: string): Promise<AzureResponse> {
    return this.gateway.copyBlob(sourcePath, destinationPath).then(() => this.gateway.deleteBlob(sourcePath))
  }

  moveDirectory(sourcePath: string, destinationPath: string): Promise<AzureResponse[]> {
    const prefix = this.getDirectoryBlobName(sourcePath)
    const destinationKey = this.getDirectoryBlobName(destinationPath)
    return this.executeActionForEachEntry(prefix, (entry: any) =>
      this.copyEntry(entry, prefix, destinationKey).then(() => this.gateway.deleteBlob(entry.name)),
    )
  }

  downloadFile(path: string): void {
    this.gateway.getBlobUrl(path).then(
      (accessUrls: AccessUrls) => {
        window.location.href = accessUrls.url1 ?? ''
      },
      (e) => new Error(e),
    )
  }

  executeActionForEachEntry(
    prefix: string,
    action: ({ method, urlPath, queryString }: { method: string; urlPath: string; queryString: string }) => void,
  ): Promise<AzureResponse[]> {
    return this.gateway.getBlobList(prefix).then((entries: any[]) => {
      const deferreds = entries.map((entry) => action(entry) as unknown as Promise<AzureResponse>)
      return Promise.all(deferreds)
    })
  }

  getDataObjectsFromEntries(entries: FileEntry[], prefix: string): FileSystemItem[] {
    const result: FileSystemItem[] = []
    const directories: Record<string, FileSystemItem> = {}

    entries.forEach((entry) => {
      const restName = entry.name?.substring(prefix.length) ?? ''
      const parts = restName?.split('/') || ''

      if (parts.length === 1) {
        if (restName !== this.EMPTY_DIR_DUMMY_BLOB_NAME && entry.length !== 0) {
          const obj: FileSystemItem = {
            name: restName,
            isDirectory: false,
            dateModified: entry.lastModified ?? new Date(),
            size: entry.length,
            path: '',
            pathKeys: [],
            key: '',
            hasSubDirectories: false,
            thumbnail: '',
            dataItem: undefined,
            getFileExtension(): string {
              throw new Error('Function not implemented.')
            },
          }
          result.push(obj)
        }
      } else {
        const dirName = parts[0]
        let directory = directories[dirName]
        if (!directory) {
          directory = {
            name: dirName,
            isDirectory: true,
            path: '',
            pathKeys: [],
            key: '',
            hasSubDirectories: false,
            dateModified: new Date(),
            size: 0,
            thumbnail: '',
            dataItem: undefined,
            getFileExtension(): string {
              throw new Error('Function not implemented.')
            },
          }
          directories[dirName] = directory
          result.push(directory)
        }

        if (!directory.hasSubDirectories) {
          directory.hasSubDirectories = parts.length > 2
        }
      }
    })

    result.sort(this.compareDataObjects)

    return result
  }

  compareDataObjects(obj1: FileSystemItem, obj2: FileSystemItem): number {
    if (obj1.isDirectory === obj2.isDirectory) {
      const name1 = obj1.name.toLowerCase()
      const name2 = obj2.name.toLowerCase()
      if (name1 < name2) {
        return -1
      }
      return name1 > name2 ? 1 : 0
    }

    return obj1.isDirectory ? -1 : 1
  }

  getDirectoryBlobName(path: string): string {
    return path ? `${path}/` : path
  }
}
